import { useCallback, useEffect, useState } from "@wordpress/element";
import { __, _x } from "@wordpress/i18n";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Geolocator } from "../../../react/components/Geolocator.jsx";
import { PlaceAutocomplete } from "../../../react/components/PlaceAutocomplete.jsx";
import SlidingPane from "react-sliding-pane";
import StoresList from "../../../react/components/StoresList.jsx";
import { useCookie } from "../../../react/services/cookie.js";
import { useSearchStores } from "../../../react/services/search.js";

export default function StoresLocator({ googleMapsApiKey, countries }) {
  const [isOpened, setOpened] = useState(false);
  const [address, setAddress] = useState("");
  const [search, setSearch] = useState("");
  const [activeStore, setActiveStore] = useState(window.__ACTIVE_STORE__);
  const [{ data: storesFound, loading, error }, searchStores] =
    useSearchStores();
  const [, setActiveStoreId] = useCookie("active-store", "");

  const open = useCallback(() => {
    setOpened(true);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'event': 'agency_search_open' });
  }, [setOpened]);
  const close = useCallback(() => setOpened(false), [setOpened]);

  useEffect(
    function () {
      if (typeof window === "undefined") return;
      const fn = function (evt) {
        setActiveStore(evt.detail);
      };
      window.addEventListener("store_selected", fn);
      return () => window.removeEventListener("store_selected", fn);
    },
    [setActiveStore]
  );

  const chooseStore = useCallback(
    function (store) {
      setActiveStore(store);
      setActiveStoreId(store.id);
      close();

      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("store_selected", { detail: store })
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'agency_selected',
        'selected_agency_id': store.id,
        'selected_agency_name': store.name,
        'selected_agency_city': store.city
      });
    },
    [setActiveStore, setActiveStoreId, close]
  );

  const selectPlace = useCallback(function (place) {
    if (!place || (!place.location && !place.geometry)) {
      setSearch("");
      return;
    }
    const location = place.location || place.geometry.location;
    const lat = location.lat();
    const lng = location.lng();
    setAddress(""); // Pour effacer l'ancien résultat mis en dur
    setSearch(`${lat},${lng}`);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'agency_search',
      'search_value': place.formatted_address || place.name || ''
    });
  }, []);

  const selectGeolocation = useCallback(function (address) {
    if (!address) return;
    if (typeof address === "string") {
      // Cas particulier sans résultat
      setSearch(address);
      setAddress(address);
      return;
    }
    const lat = address.geometry.location.lat();
    const lng = address.geometry.location.lng();
    setAddress(address.formatted_address);
    setSearch(`${lat},${lng}`);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'agency_search',
      'search_type': 'geolocation',
      'search_value': typeof address === 'string' ? address : address.formatted_address
    });
  }, []);

  useEffect(
    function () {
      const elements = document.querySelectorAll('[data-role="manitoba-stores-locator-trigger"]');
      elements.forEach(function (el) {
        el.addEventListener("click", open);
      });
      return function () {
        elements.forEach(function (el) {
          el.removeEventListener("click", open);
        });
      };
    },
    [open]
  );

  useEffect(
    function () {
      searchStores(search);
    },
    [search, searchStores]
  );

  return (
    <>
      <span
        className={
          "stores-locator" + (activeStore ? " stores-locator--active" : "")
        }
      >
        <button
          className={
            "stores-locator-trigger" + (activeStore
              ? " stores-locator-trigger--active"
              : "")
          }
          onClick={open}
        >
          <span className="stores-locator-trigger-content">
            <span className="stores-locator-trigger-label">
              {activeStore
                ? activeStore.name
                : _x('Trouver votre agence', 'Frontend stores locator', 'manitoba-stores')}
            </span>
            <span className="stores-locator-trigger-explain">
              {activeStore
                ? _x('Modifier mon agence', 'store locator', 'manitoba-stores')
                : _x('Me localiser', 'Frontend stores locator', 'manitoba-stores')}
            </span>
          </span>
        </button>
      </span>
      <SlidingPane
        closeIcon={
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19.3611 2.74348L12.0366 10.068L19.2927 17.3241L17.2847 19.3322L10.0285 12.0761L2.74408 19.3605L0.639335 17.2557L7.92378 9.9713L0.667647 2.71517L2.67572 0.7071L9.93185 7.96323L17.2563 0.638737L19.3611 2.74348Z"
              fill="white"
              stroke="black"
              strokeWidth="0.5"
            />
          </svg>
        }
        className="stores-locator-panel"
        overlayClassName="stores-locator-overlay"
        isOpen={isOpened}
        title={_x('Trouver une agence', 'store locator', 'manitoba-stores')}
        onRequestClose={close}
        width="470px"
      >
        <APIProvider apiKey={googleMapsApiKey}>
          <div className="stores-search">
            <span className="stores-search-input">
              <PlaceAutocomplete
                options={{
                  componentRestrictions: countries
                    ? {
                        country: countries
                          .split(",")
                          .map((country) => country.trim().toLowerCase()),
                      }
                    : {},
                }}
                onChange={selectPlace}
                value={address}
                placeholder={_x('Saisissez une ville ou un code postal', 'store locator', 'manitoba-stores')}
              />
              <span className="stores-search-geolocation">
                <Geolocator
                  className="stores-search-geolocation-trigger"
                  triggerOnLoad
                  onChange={selectGeolocation}
                  title={_x('Me géolocaliser', 'Frontend stores locator', 'manitoba-stores')}
                />
              </span>
            </span>
          </div>
          {!!error && (
            <div className="stores-search-error">
              <p>{typeof error?.message === 'string' ? error.message : _x('Une erreur est survenue', 'store locator', 'manitoba-stores')}</p>
            </div>
          )}
          {!!loading && (
            <div className="stores-search-loading">
              <p>
                {_x('Chargement...', 'store locator', 'manitoba-stores')}
              </p>
            </div>
          )}
          <StoresList
            list={storesFound}
            activeId={activeStore ? activeStore.id : null}
            onChoose={chooseStore}
          />
        </APIProvider>
      </SlidingPane>
    </>
  );
}
